import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import JSZip from 'jszip';

interface CustomDicomTag {
  tag: string;
  vr: string;
  value: string | number;
}
// Configure cornerstone
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
cornerstoneWADOImageLoader.configure({
  beforeSend: (xhr: any) => {
    xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
  },
  useWebWorkers: false,
});

const sopClassUIDs = {
  enhanced: {
    CT: '1.2.840.10008.5.1.4.1.1.2.1',
    MR: '1.2.840.10008.5.1.4.1.1.4.1',
    US: '1.2.840.10008.5.1.4.1.1.6.2',
    XA: '1.2.840.10008.5.1.4.1.1.12.1.1',
    XRF: '1.2.840.10008.5.1.4.1.1.12.2.1',
    SR: '1.2.840.10008.5.1.4.1.1.88.22',
    PET: '1.2.840.10008.5.1.4.1.1.130',
  },
};

export const getSopType = (SOPClassUID: string): 'normal' | 'multiframe' => {
  if (Object.values(sopClassUIDs.enhanced).includes(SOPClassUID)) {
    return 'multiframe';
  }
  return 'normal';
};

export const createModifiedDicomBlob = (
  originalBuffer: ArrayBuffer,
  customTags: CustomDicomTag[]
): Blob => {
  const byteArray = new Uint8Array(originalBuffer);
  // Calculate additional space needed for custom tags
  const additionalSpace = customTags.reduce((total, tag) => {
    // 8 bytes for tag header (tag + VR + length) + value length
    return total + 8 + tag.value.toString().length;
  }, 0);

  // Create new buffer with additional space
  const newBuffer = new ArrayBuffer(byteArray.length + additionalSpace);
  const newByteArray = new Uint8Array(newBuffer);

  // Copy original data
  newByteArray.set(byteArray);

  let offset = byteArray.length;

  // Add custom tags
  customTags.forEach(({ tag, vr, value }) => {
    const group = parseInt(tag.slice(1, 5), 16);
    const element = parseInt(tag.slice(5, 9), 16);
    const valueStr = value.toString();

    // Write tag group and element
    newByteArray[offset] = group & 0xff;
    newByteArray[offset + 1] = (group >> 8) & 0xff;
    newByteArray[offset + 2] = element & 0xff;
    newByteArray[offset + 3] = (element >> 8) & 0xff;

    // Write VR
    newByteArray[offset + 4] = vr.charCodeAt(0);
    newByteArray[offset + 5] = vr.charCodeAt(1);

    // Write length
    newByteArray[offset + 6] = valueStr.length & 0xff;
    newByteArray[offset + 7] = (valueStr.length >> 8) & 0xff;

    // Write value
    for (let i = 0; i < valueStr.length; i++) {
      newByteArray[offset + 8 + i] = valueStr.charCodeAt(i);
    }

    offset += 8 + valueStr.length;
  });

  return new Blob([newBuffer], { type: 'application/dicom' });
};

export const getTagValue = (dataSet: any, ...tags: string[]) => {
  try {
    let element;
    let i = 0;
    if (tags.length === 1) {
      element = dataSet?.elements[tags[0]];
    } else if (tags.length > 1) {
      for (; i < tags.length - 1; i++) {
        element = dataSet.elements[tags[i]].items[0];
        if (i === tags.length) {
          continue;
        }
        dataSet = element.dataSet;
      }
    }
    return element ? dataSet?.string(tags[i]) : '';
  } catch (error) {
    return '';
  }
};

export const getNumericTagValue = (dataSet: any, tag: string): number => {
  try {
    const element = dataSet?.elements[tag];
    if (!element) {
      console.debug(`Tag ${tag} not found in dataset`);
      return 0;
    }

    // Get the raw value directly from the element
    let value = 0;

    // Handle different VR (Value Representation) types
    switch (element.vr) {
      case 'US':
        value = dataSet.uint16(tag);
        break;
      case 'SS':
        value = dataSet.int16(tag);
        break;
      case 'UL':
        value = dataSet.uint32(tag);
        break;
      case 'SL':
        value = dataSet.int32(tag);
        break;
      case 'FL':
        value = dataSet.float(tag);
        break;
      case 'FD':
        value = dataSet.double(tag);
        break;
      case 'IS':
      case 'DS':
        const stringValue = dataSet.string(tag);
        value = stringValue ? parseFloat(stringValue) : 0;
        break;
      default:
        const defaultValue = dataSet.string(tag);
        value = defaultValue ? parseFloat(defaultValue) : 0;
    }
    return isNaN(value) ? 0 : value;
  } catch (error) {
    console.warn(`Error parsing numeric tag ${tag}:`, error);
    return 0;
  }
};

export const extractCommonMetadataFields = (dataSet: any) => {
  return {
    metadata: {
      Columns: getNumericTagValue(dataSet, 'x00280011') || 0,
      Rows: getNumericTagValue(dataSet, 'x00280010') || 0,
      InstanceNumber: getNumericTagValue(dataSet, 'x00200013') || 0,
      SOPClassUID: getTagValue(dataSet, 'x00080016'),
      PhotometricInterpretation: getTagValue(dataSet, 'x00280004'),
      AcquisitionNumber: getTagValue(dataSet, 'x00200012'),
      BitsAllocated: getNumericTagValue(dataSet, 'x00280100') || 0,
      BitsStored: getNumericTagValue(dataSet, 'x00280101') || 0,
      PixelRepresentation: getNumericTagValue(dataSet, 'x00280103') || 0,
      SamplesPerPixel: getNumericTagValue(dataSet, 'x00280002') || 0,
      HighBit: getNumericTagValue(dataSet, 'x00280102') || 0,
      ImageOrientationPatient:
        getTagValue(dataSet, 'x00200037')?.split('\\').map(Number) || [],
      ImagePositionPatient:
        getTagValue(dataSet, 'x00200032')?.split('\\').map(Number) || [],
      FrameOfReferenceUID: getTagValue(dataSet, 'x00200052'),
      ImageType: getTagValue(dataSet, 'x00080008')?.split('\\').join(',') || [],
      Modality: getTagValue(dataSet, 'x00080060'),
      SOPInstanceUID: getTagValue(dataSet, 'x00080018'),
      SOPType: null as 'normal' | 'multiframe' | null,
      SeriesInstanceUID: getTagValue(dataSet, 'x0020000e'),
      StudyInstanceUID: getTagValue(dataSet, 'x0020000d'),
      SeriesDate: getTagValue(dataSet, 'x00080021'),
      InstitutionName: getTagValue(dataSet, 'x00080080'),
      NumberOfPhaseEncodingSteps: getTagValue(dataSet, 'x00180089'),
      SeriesDescription: getTagValue(dataSet, 'x0008103e'),
      StudyID: getTagValue(dataSet, 'x00200010'),
      StudyDate: getTagValue(dataSet, 'x00080020'),
    },
    url: '',
  };
};

export const extractMetadataForDicom = (dataSet: any, SOPType: string) => {
  if (SOPType === 'normal') {
    return {
      PixelSpacing:
        getTagValue(dataSet, 'x00280030')?.split('\\').map(Number) || [],
      WindowCenter:
        parseFloat(getTagValue(dataSet, 'x00281050') as string) || 0,
      WindowWidth: parseFloat(getTagValue(dataSet, 'x00281051') as string) || 0,
      SliceThickness:
        parseFloat(getTagValue(dataSet, 'x00180050') as string) || 0,
    };
  } else {
    return {
      SliceThickness:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00289110', 'x00180050') as string
        ) || 0,
      PixelSpacing:
        (getTagValue(
          dataSet,
          'x52009230',
          'x00289110',
          'x00280030'
        ) as string) || '',
      EchoTime:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00189114', 'x00189082') as string
        ) || 0,
      SpacingBetweenSlices:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00289110', 'x00180088') as string
        ) || 0,
      RFEchoTrainLength:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00189112', 'x00189240') as string
        ) || 0,
      RepetitionTime:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00189112', 'x00180080') as string
        ) || 0,
      EchoTrainLength:
        parseFloat(
          getTagValue(dataSet, 'x52009229', 'x00189112', 'x00180091') as string
        ) || 0,
      FlipAngle:
        parseFloat(
          getTagValue(dataSet, 'x52009229', 'x00189112', 'x00181314') as string
        ) || 0,
      GradientEchoTrainLength:
        parseFloat(
          getTagValue(dataSet, 'x52009229', 'x00189112', 'x00189241') as string
        ) || 0,
      InversionTime:
        parseFloat(
          getTagValue(dataSet, 'x52009229', 'x00189115', 'x00189079') as string
        ) || 0,
      RescaleSlope:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00289145', 'x00281053') as string
        ) || 0,
      RescaleIntercept:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00289145', 'x00281052') as string
        ) || 0,
      WindowCenter:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00289132', 'x00281050') as string
        ) || 0,
      WindowWidth:
        parseFloat(
          getTagValue(dataSet, 'x52009230', 'x00289132', 'x00281051') as string
        ) || 0,
    };
  }
};

export const organizeMetadata = (dicomFiles: any[]) => {
  const studies = new Map();
  dicomFiles.forEach(({ dataSet }: any) => {
    const studyInstanceUID = getTagValue(dataSet, 'x0020000d');
    const seriesInstanceUID = getTagValue(dataSet, 'x0020000e');
    if (!studies?.has(studyInstanceUID)) {
      studies?.set(studyInstanceUID, {
        StudyInstanceUID: studyInstanceUID,
        StudyDescription: getTagValue(dataSet, 'x00081030'),
        StudyDate: getTagValue(dataSet, 'x00080020'),
        StudyTime: getTagValue(dataSet, 'x00080030'),
        PatientName: getTagValue(dataSet, 'x00100010'),
        PatientID: getTagValue(dataSet, 'x00100020'),
        AccessionNumber: getTagValue(dataSet, 'x00080050'),
        PatientAge: getTagValue(dataSet, 'x00101010'),
        PatientSex: getTagValue(dataSet, 'x00100040'),
        series: new Map(),
        NumInstances: 0,
        Modalities: new Set(),
      });
    }
    const study = studies?.get(studyInstanceUID);
    if (!study?.series?.has(seriesInstanceUID)) {
      study?.series?.set(seriesInstanceUID, {
        SeriesInstanceUID: seriesInstanceUID,
        SeriesNumber: getNumericTagValue(dataSet, 'x00200011') || 0,
        SeriesDescription: getTagValue(dataSet, 'x0008103e'),
        SeriesTime: getTagValue(dataSet, 'x00080030'),
        Modality: getTagValue(dataSet, 'x00080060'),
        instances: [],
      });
    }
    const series = study?.series?.get(seriesInstanceUID);
    const instanceMetadata = extractCommonMetadataFields(dataSet);
    instanceMetadata.metadata = {
      ...instanceMetadata.metadata,
      ...extractMetadataForDicom(
        dataSet,
        getSopType(instanceMetadata.metadata.SOPClassUID) as string
      ),
    };
    const SOPClassUID = instanceMetadata.metadata.SOPClassUID;
    instanceMetadata.metadata.SOPType = getSopType(SOPClassUID as string);
    series?.instances?.push(instanceMetadata);
    study.NumInstances++;
    study?.Modalities?.add(getTagValue(dataSet, 'x00080060'));
  });
  const studiesArray = Array.from(studies.values())?.map((study) => ({
    ...study,
    series: Array.from(study?.series?.values()),
    Modalities: Array.from(study?.Modalities)?.join('/'),
  }));
  return { studies: studiesArray };
};

export const loadDicomFile = async (
  arrayBuffer: any,
  fileName: string = 'dicom_file.dcm'
) => {
  try {
    const byteArray = new Uint8Array(arrayBuffer);
    const dataSet = dicomParser.parseDicom(byteArray);
    const blob = new Blob([arrayBuffer]);
    new File([blob], fileName);
    return { dataSet };
  } catch (error: any) {
    throw new Error('Error parsing DICOM file: ' + error.message);
  }
};

export const isSupportedOtherFile = (fileName: string): boolean => {
  const lowerName = fileName.toLowerCase();
  const supportedExtensions = [
    '.odf',
    '.doc',
    '.docx',
    '.pdf',
    '.exar',
    '.exar1',
    '.hd5',
    '.sr',
  ];
  return supportedExtensions.some((ext) => lowerName.endsWith(ext));
};

// export const processZipFileWithSeparation = async (file: File) => {
//   const zip = new JSZip();
//   const arrayBuffer = await file.arrayBuffer();
//   const zipContent = await zip.loadAsync(arrayBuffer);

//   const dicomFiles: any[] = [];
//   const otherFiles: File[] = [];

//   for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
//     if (!zipEntry.dir) {
//       const fileData = await zipEntry.async('arraybuffer');
//       const fileNameLowerCase = filename.toLowerCase();
//       const fileNameOnly = filename.split('/').pop() || 'unknown_file';
//       const isDicom =
//         fileNameLowerCase.endsWith('.dcm') ||
//         fileNameLowerCase.endsWith('.dicom') ||
//         fileNameLowerCase.endsWith('.ima') ||
//         !filename.includes('.');

//       if (isDicom) {
//         try {
//           const dicomFile = await loadDicomFile(fileData);
//           dicomFiles.push(dicomFile);
//         } catch (error) {
//           console.warn(`Failed to load DICOM file ${filename}:`, error);
//         }
//       } else if (isSupportedOtherFile(filename)) {
//         const blob = new Blob([fileData]);
//         const file = new File([blob], fileNameOnly);
//         otherFiles.push(file);
//       }
//     }
//   }

//   return { dicomFiles, otherFiles };
// };
export const processZipFileWithSeparation = async (file: File) => {
  // Size limits for ZIP file processing
  const MAX_RECOMMENDED_SIZE_MB = 500; // 500MB recommended limit
  const MAX_HARD_LIMIT_MB = 2000; // 2GB hard limit (browser dependent)
  const fileSizeMB = file.size / (1024 * 1024);
  
  // Check file size before processing
  if (file.size > MAX_HARD_LIMIT_MB * 1024 * 1024) {
    const errorMessage = 
      `ZIP file "${file.name}" is too large (${fileSizeMB.toFixed(2)}MB). ` +
      `Maximum supported size is ${MAX_HARD_LIMIT_MB}MB.\n\n` +
      `Please split your ZIP file into smaller archives:\n` +
      `• Recommended: Split into files of ${MAX_RECOMMENDED_SIZE_MB}MB or less\n` +
      `• You can upload multiple ZIP files at once\n\n` +
      `How to split ZIP files:\n` +
      `• Windows: Use 7-Zip or WinRAR (Right-click → Split archive)\n` +
      `• Mac: Use Archive Utility or The Unarchiver\n` +
      `• Online: Use online ZIP splitter tools\n\n` +
      `After splitting, you can upload all the smaller ZIP files together.`;
    
    throw new Error(errorMessage);
  }
  
  // Warn for large files but still allow processing
  if (file.size > MAX_RECOMMENDED_SIZE_MB * 1024 * 1024) {
    console.warn(
      `Large ZIP file detected: ${fileSizeMB.toFixed(2)}MB. ` +
      `Processing may take longer and use significant memory. ` +
      `Consider splitting into smaller files (< ${MAX_RECOMMENDED_SIZE_MB}MB) for better performance.`
    );
  }

  const zip = new JSZip();
  let arrayBuffer: ArrayBuffer;
  
  try {
    // Attempt to load the entire ZIP file into memory
    arrayBuffer = await file.arrayBuffer();
  } catch (error: any) {
    // Handle memory allocation errors with helpful guidance
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    if (errorMessage.includes('allocation') || errorMessage.includes('buffer')) {
      const splitInstructions = 
        `Array buffer allocation failed: The ZIP file "${file.name}" (${fileSizeMB.toFixed(2)}MB) is too large to process in the browser.\n\n` +
        `This error occurs when the browser cannot allocate enough memory. ` +
        `Please split your ZIP file into smaller archives:\n\n` +
        `Recommended Solution:\n` +
        `1. Split the ZIP file into multiple archives of ${MAX_RECOMMENDED_SIZE_MB}MB or less\n` +
        `2. Upload all the smaller ZIP files together (you can select multiple files)\n\n` +
        `How to split ZIP files:\n` +
        `• Windows: Use 7-Zip (Right-click → 7-Zip → Split file)\n` +
        `• Mac: Use Archive Utility or command line: zip -s 500m largefile.zip split_\n` +
        `• Online: Use online ZIP splitter tools\n\n` +
        `After splitting, you can drag and drop all the smaller ZIP files at once.`;
      
      throw new Error(splitInstructions);
    }
    throw new Error(`Error reading ZIP file "${file.name}": ${errorMessage}`);
  }

  let zipContent;
  try {
    zipContent = await zip.loadAsync(arrayBuffer);
  } catch (error: any) {
    throw new Error(
      `Error parsing ZIP file "${file.name}": ${error?.message || 'Invalid or corrupted ZIP file'}`
    );
  }

  const dicomFiles: any[] = [];
  const otherFiles: File[] = [];

  for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
    if (!zipEntry.dir) {
      try {
        const fileData = await zipEntry.async('arraybuffer');
        const fileNameLowerCase = filename.toLowerCase();
        const fileNameOnly = filename.split('/').pop() || 'unknown_file';
        const isDicom =
          fileNameLowerCase.endsWith('.dcm') ||
          fileNameLowerCase.endsWith('.dicom') ||
          fileNameLowerCase.endsWith('.ima') ||
          !filename.includes('.');

        if (isDicom) {
          try {
            const dicomFile = await loadDicomFile(fileData);
            dicomFiles.push(dicomFile);
          } catch (error) {
            console.warn(`Failed to load DICOM file ${filename}:`, error);
          }
        } else if (isSupportedOtherFile(filename)) {
          const blob = new Blob([fileData]);
          const file = new File([blob], fileNameOnly);
          otherFiles.push(file);
        }
      } catch (error) {
        console.warn(`Failed to extract file ${filename} from ZIP:`, error);
      }
    }
  }

  return { dicomFiles, otherFiles };
};

export const isInstanceInContinuityOrder = (
  sortedInstances: any,
  groupLevelInstanceCount: any
) => {
  let continuityCheckCount: number;
  !groupLevelInstanceCount
    ? (continuityCheckCount = sortedInstances?.length)
    : (continuityCheckCount = groupLevelInstanceCount);
  if (sortedInstances?.length !== continuityCheckCount) {
    return true;
  } else {
    for (let i = 1; i < continuityCheckCount; i++) {
      const currentInstanceNumber = parseInt(
        sortedInstances[i]?.metadata?.InstanceNumber
      );
      const previousInstanceNumber = parseInt(
        sortedInstances[i - 1]?.metadata?.InstanceNumber
      );
      if (currentInstanceNumber - previousInstanceNumber !== 1) {
        return true;
      }
    }
  }
  return false;
};

const checkAndCondition = (
  values: string[],
  flags: string,
  metadataValue: any
) => {
  return values.every((value) => new RegExp(value, flags).test(metadataValue));
};

const checkOrCondition = (
  values: string[],
  flags: string,
  metadataValue: any
) => {
  return values.some((value) => new RegExp(value, flags).test(metadataValue));
};

export const validateSeriesDescription = (
  metadataValue: string,
  element: any
) => {
  const flags = element?.options ? element?.options : 'i';
  let result: boolean;
  if (element?.constraint === 'AND') {
    result = element?.value?.every((valueSet: any) => {
      return valueSet?.constraint === 'AND'
        ? checkAndCondition(valueSet?.value, flags, metadataValue)
        : checkOrCondition(valueSet?.value, flags, metadataValue);
    });
  } else if (element?.constraint === 'OR') {
    result = element?.value?.some((valueSet: any) => {
      return valueSet?.constraint === 'AND'
        ? checkAndCondition(valueSet?.value, flags, metadataValue)
        : checkOrCondition(valueSet?.value, flags, metadataValue);
    });
  } else {
    result = element?.value?.some((valueSet: any) => {
      return valueSet?.constraint === 'AND'
        ? checkAndCondition(valueSet?.value, flags, metadataValue)
        : checkOrCondition(valueSet?.value, flags, metadataValue);
    });
  }
  return result;
};

export const handleToValidateQualifierPhantom = async (
  studyData: any,
  validatorJson: any
) => {
  const data = validatorJson;
  const qualifierPhantomValidatorData: any = data;
  const scanTypes = Object.keys(qualifierPhantomValidatorData?.scan || {});
  const allValidatedSeries: any[] = [];
  let processedSeries: any;
  const firstLevelSatisfiedSeries: any = [];
  scanTypes?.forEach((scanType) => {
    const validatorData: any[] =
      qualifierPhantomValidatorData?.scan?.[scanType]?.match || [];
    const groupLevelCheck =
      qualifierPhantomValidatorData?.scan?.[scanType]?.group?.instanceNumber;
    console.log('Group level check', groupLevelCheck);
    if (!validatorData) return;
    const allSeries = studyData?.studies || [];
    let isMatchWithArray = false;
    const totalCriteria: number = validatorData.length;
    allSeries?.forEach((seriesData: any) => {
      seriesData?.series?.forEach((series: any) => {
        series.instances?.forEach((instanceData: any) => {
          let matchedCriteria: number = 0;
          const scanResults: { scanType: string; unmatched: string[] }[] = [];
          let unMatchedItems: any[] = [];
          validatorData?.forEach((element: any) => {
            Object.entries(instanceData?.metadata)?.forEach(
              ([metadataKey, metadataValue]: any) => {
                if (
                  element?.field?.toLowerCase() === metadataKey?.toLowerCase()
                ) {
                  if (element.field === 'SeriesDescription') {
                    if (validateSeriesDescription(metadataValue, element)) {
                      matchedCriteria++;
                    } else {
                      unMatchedItems.push(metadataKey);
                    }
                  } else {
                    if (Array.isArray(element?.value)) {
                      const arrayMatch =
                        element?.constraint === 'OR'
                          ? checkOrCondition(
                              element?.value,
                              element?.options,
                              metadataValue
                            )
                          : checkAndCondition(
                              element?.value,
                              element?.options,
                              metadataValue
                            );
                      if (arrayMatch) matchedCriteria++;
                      else unMatchedItems.push(metadataKey);
                    } else {
                      const elementValue =
                        typeof element?.value === 'string'
                          ? element?.value
                          : element?.value?.toString();
                      const metadataElementValue =
                        typeof metadataValue === 'string'
                          ? metadataValue
                          : metadataValue?.toString();
                      const singleMatch =
                        elementValue?.toLowerCase() ===
                        metadataElementValue?.toLowerCase();
                      if (singleMatch) matchedCriteria++;
                      else unMatchedItems.push(metadataKey);
                    }
                  }
                }
              }
            );
          });
          isMatchWithArray = matchedCriteria === totalCriteria;
          scanResults.push({
            scanType,
            unmatched: unMatchedItems,
          });
          if (!instanceData.unMatchedItems) {
            instanceData.unMatchedItems = scanResults;
          } else {
            instanceData.unMatchedItems.push(...scanResults);
          }
        });
        if (isMatchWithArray) {
          const existingSeriesIndex = firstLevelSatisfiedSeries?.findIndex(
            (item: any) => {
              return item?.SeriesInstanceUID === series?.SeriesInstanceUID;
            }
          );
          const sortedInstances = series.instances.sort(
            (a: any, b: any) =>
              parseInt(a.metadata.InstanceNumber) -
              parseInt(b.metadata.InstanceNumber)
          );
          const isInvalidDicom = isInstanceInContinuityOrder(
            sortedInstances,
            groupLevelCheck
          );
          if (existingSeriesIndex === -1) {
            const enhancedSeriesData = {
              ...series,
              failedScanTypes: [],
              isInvalidDicom,
            };
            firstLevelSatisfiedSeries?.push(enhancedSeriesData);
          } else {
            firstLevelSatisfiedSeries[existingSeriesIndex].isInvalidDicom =
              isInvalidDicom;
          }
        } else {
          const data = {
            ...series,
            failedScanTypes: [scanType],
          };
          const existingSeriesIndex = firstLevelSatisfiedSeries?.findIndex(
            (item: any) => {
              return item?.SeriesInstanceUID === series?.SeriesInstanceUID;
            }
          );
          if (existingSeriesIndex === -1) {
            firstLevelSatisfiedSeries?.push(data);
          } else {
            firstLevelSatisfiedSeries[
              existingSeriesIndex
            ]?.failedScanTypes?.push(scanType);
          }
        }
      });
    });
    // Group series by SeriesDescription
    const seriesGroups = firstLevelSatisfiedSeries?.reduce(
      (groups: any, series: any) => {
        const description = series?.SeriesDescription;
        if (!groups[description]) {
          groups[description] = [];
        }
        groups[description]?.push(series);
        return groups;
      },
      {}
    );
    // Add isDuplicate field for series with same description but lower SeriesNumber
    Object.values(seriesGroups)?.forEach((item: any) => {
      if (item?.length > 1) {
        item?.sort(
          (a: any, b: any) => Number(a?.SeriesNumber) - Number(b?.SeriesNumber)
        );
        item?.forEach((series: any, index: number) => {
          if (index < item?.length - 1) {
            series.isDuplicate = true;
          }
        });
      }
    });
    processedSeries = Object.values(seriesGroups)?.flat();
  });
  processedSeries?.forEach((series: any) => {
    if (scanTypes?.length === series?.failedScanTypes?.length) {
      series.isInvalidDicom = true;
    }
  });
  allValidatedSeries?.push(...processedSeries);
  return allValidatedSeries;
};

export const formatDate = (date: any) => {
  const dateStr = date;
  const formatted = `${dateStr?.slice(4, 6)}/${dateStr?.slice(
    6,
    8
  )}/${dateStr?.slice(0, 4)}`;
  return formatted;
};

export const handleGroupBySeries = (series: any, validatorJson: any) => {
  const groupItem = validatorJson?.groupBy;
  const result: { item: any[]; groupValue: string }[] = [];
  let nonMatchedItems: any[] = [...(series || [])];
  let nonGroupedData: any[] = [];
  groupItem?.value?.forEach((groupValue: string) => {
    let groupedMenuItem: any = [];
    series?.forEach((seriesData: any) => {
      const groupField = groupItem?.field;
      if (seriesData[groupField]?.includes(groupValue)) {
        groupedMenuItem?.push(seriesData);
        return seriesData;
      }
    });
    if (groupedMenuItem && groupedMenuItem.length > 0) {
      result.push({
        groupValue,
        item: groupedMenuItem,
      });
      groupedMenuItem?.forEach((matchedItem: any) => {
        nonMatchedItems = nonMatchedItems?.filter(
          (item: any) => item !== matchedItem
        );
      });
    }
  });
  if (nonMatchedItems?.length > 0) {
    nonGroupedData = nonMatchedItems;
  }

  return { result, nonGroupedData };
};

export const isNumericField = (fieldId: string): boolean => {
  return (
    fieldId.toLowerCase().includes('humidity') ||
    fieldId.toLowerCase().includes('temperature')
  );
};

export function sortSeries(arr: any) {
  return arr.slice().sort((a: any, b: any) => {
    const aError = a.isDuplicate || a.isInvalidDicom;
    const bError = b.isDuplicate || b.isInvalidDicom;
    if (aError === bError) return 0;
    return aError ? 1 : -1;
  });
}

export function getDropAreaMessage(studyFields: { studyCondition?: string } | null) {
  if (studyFields?.studyCondition === "Human Subject CRF or Study Material") {
    return "Drag and drop files here (CRFs and/or study materials, zip files or drag all at once)";
  }
  return "Drag and drop files here (Zipped Folder with DICOMS)";
}
export interface UploadPortalResponse {
  ok: boolean;
  status: number;
  statusText: string;
  successfulFiles: { fileName: string; sopInstanceUID: string }[];
  failedFiles: { fileName: string; sopInstanceUID: string; error: string; code: number }[];
}

export async function uploadPortal(
  authToken: string,
  blob: Blob,
  boundary: string,
  sopToFileName: Map<string, string>
): Promise<UploadPortalResponse> {
  try {
    const response = await fetch("YOUR_DICOM_STORE_ENDPOINT", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": `multipart/related; type="application/dicom"; boundary=${boundary}`,
      },
      body: blob,
    });

    const successfulFiles: { fileName: string; sopInstanceUID: string }[] = [];
    const failedFiles: { fileName: string; sopInstanceUID: string; error: string; code: number }[] = [];

    // Parse response body as JSON (DICOMweb response)
    const responseData = await response.json();

    // Parse ReferencedSOPSequence for successful files
    if (responseData["00081199"]?.Value) {
      responseData["00081199"].Value.forEach((item: any) => {
        const sopInstanceUID = item["00081155"]?.Value?.[0] || "";
        const fileName = sopToFileName.get(sopInstanceUID) || "Unknown file";
        successfulFiles.push({ fileName, sopInstanceUID });
      });
    }

    // Parse FailedSOPSequence for failed files
    if (responseData["00081198"]?.Value) {
      responseData["00081198"].Value.forEach((item: any) => {
        const sopInstanceUID = item["00081155"]?.Value?.[0] || "";
        const fileName = sopToFileName.get(sopInstanceUID) || "Unknown file";
        const error = item["00090097"]?.Value?.[0] || "Unknown error";
        const code = item["00081197"]?.Value?.[0] || 0;
        failedFiles.push({ fileName, sopInstanceUID, error, code });
      });
    }

    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      successfulFiles,
      failedFiles,
    };
  } catch (error: any) {
    return {
      ok: false,
      status: 0,
      statusText: error.message || "Network error",
      successfulFiles: [],
      failedFiles: [],
    };
  }
}